import { h } from 'preact';
import { useEffect, useRef } from 'preact/hooks';
import { GraphEdge, VertexPreview } from '../../types/graph';
import { EditableNote } from '../EditableNote';
import { VertexLink } from '../shared/VertexLink';

export interface EdgeInfoModalProps {
  edge: GraphEdge;
  preview: VertexPreview | undefined;
  vertexUrl: string;
  onClose: () => void;
  onUpdateInfo: (edge: GraphEdge, info: string | null) => Promise<void>;
}

export function EdgeInfoModal({ edge, preview, vertexUrl, onClose, onUpdateInfo }: EdgeInfoModalProps) {
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  function handleBackdropClick(e: MouseEvent) {
    if (e.target === backdropRef.current) {
      onClose();
    }
  }

  async function handleSaveInfo(newInfo: string | null) {
    await onUpdateInfo(edge, newInfo);
  }

  return (
    <div class="edge-info-modal-backdrop" ref={backdropRef} onClick={handleBackdropClick}>
      <div class="edge-info-modal">
        <button class="edge-info-modal-close" onClick={onClose} title="Close">
          &times;
        </button>

        {/* Vertex preview */}
        <div class="edge-info-modal-preview">
          {preview?.image && (
            <img
              src={preview.image}
              alt=""
              class="edge-info-modal-image"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          )}
          <div class="edge-info-modal-content">
            <div class="edge-info-modal-title">
              {preview?.title || vertexUrl}
            </div>
            {preview?.description && (
              <div class="edge-info-modal-desc">{preview.description}</div>
            )}
            <VertexLink href={vertexUrl} class="edge-info-modal-url">
              {vertexUrl}
            </VertexLink>
          </div>
        </div>

        {/* Editable note */}
        <div class="edge-info-modal-note-section">
          <div class="edge-info-modal-note-label">Note</div>
          <EditableNote
            value={edge.info || null}
            placeholder="Click to add a note..."
            className="edge-info-modal-note"
            onSave={handleSaveInfo}
          />
        </div>
      </div>
    </div>
  );
}
